import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { HelmetProvider } from 'react-helmet-async';
import { api } from '../../convex/_generated/api';
import { Doc } from '../../convex/_generated/dataModel';
import Hero from './Hero';
import ProjectOverview from './ProjectOverview';
import ProjectHighlights from './ProjectHighlights';
import Navigation from './Navigation';
import StickyActions from './StickyActions';
import { ToastProvider } from '../ui/use-toast';
import CookieConsent from '../ui/CookieConsent';
import { loadTrackingScripts } from '../../lib/tracking';
import FAQ from './FAQ';
import Location from './Location';
import PaymentPlan from './PaymentPlan';
import ProjectGallery from './ProjectGallery';
import LegalInfo from './LegalInfo';
import { DynamicLeadForm } from './DynamicLeadForm';
import { Id } from '../../convex/_generated/dataModel';
import SEOHead from '../ui/SEOHead';
import { getRTLClasses, getDirAttribute } from '../../utils/rtl';
import { useBestImageUrl, getBestImageUrl, useMultipleFileUrls } from '../../utils/fileUtils';

const DynamicLandingPage: React.FC = () => {
  const { language, projectSlug } = useParams<{ language: string; projectSlug: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  
  // RTL detection and classes
  const currentLanguage = language || 'en';
  const rtlClasses = getRTLClasses(currentLanguage);
  const dirAttribute = getDirAttribute(currentLanguage);
  
  // Get project data with explicit type annotation to break circular reference
  const project = useQuery(api.projects.getBySlug, {
    slug: projectSlug || ''
  }) as Doc<"projects"> | null | undefined;
  
  // Get page components for this project and language
  const components = useQuery(api.pageComponents.getComponentsForPublicPage, {
    projectSlug: projectSlug || '',
    languageCode: language || 'en'
  }) || [];

  // Get navigation settings for content labels
  const navigationConfig = useQuery(api.landingPageSettings.getNavigationMenuItems, 
    project ? {
      projectId: project._id,
      languageCode: language || 'en'
    } : "skip"
  );

  // Handle errors and redirects
  useEffect(() => {
    if (project === undefined && error === null) {
      // Still loading, don't do anything yet
      return;
    }
    
    if (project === null) {
      setError('Project not found or not published');
      // Redirect to homepage after a delay
      const timer = setTimeout(() => {
        navigate('/');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [project, error, navigate]);

  // Tracking scripts
  useEffect(() => {
    // Check for existing consent
    const storedConsent = localStorage.getItem('cookie-consent');
    if (storedConsent) {
      const consent = JSON.parse(storedConsent);
      loadTrackingScripts(consent, {
        googleAnalytics: process.env.NEXT_PUBLIC_GA_ID,
        googleTagManager: process.env.NEXT_PUBLIC_GTM_ID,
        googleAds: process.env.NEXT_PUBLIC_GADS_ID,
        facebookPixel: process.env.NEXT_PUBLIC_FB_PIXEL_ID
      });
    }
  }, []);

  const handleCookieAccept = (consent: { analytics: boolean; marketing: boolean }) => {
    loadTrackingScripts({ ...consent, timestamp: Date.now() }, {
      googleAnalytics: process.env.NEXT_PUBLIC_GA_ID,
      googleTagManager: process.env.NEXT_PUBLIC_GTM_ID,
      googleAds: process.env.NEXT_PUBLIC_GADS_ID,
      facebookPixel: process.env.NEXT_PUBLIC_FB_PIXEL_ID
    });
  };

  const handleCookieCustomize = (consent: { analytics: boolean; marketing: boolean }) => {
    loadTrackingScripts({ ...consent, timestamp: Date.now() }, {
      googleAnalytics: process.env.NEXT_PUBLIC_GA_ID,
      googleTagManager: process.env.NEXT_PUBLIC_GTM_ID,
      googleAds: process.env.NEXT_PUBLIC_GADS_ID,
      facebookPixel: process.env.NEXT_PUBLIC_FB_PIXEL_ID
    });
  };

  // Error state
  if (error) {
    return (
      <HelmetProvider>
        <SEOHead 
          title="Page Not Found"
          description="The requested property page could not be found. Please check the URL or return to our homepage to explore available properties."
        />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600">Error</h1>
            <p className="text-gray-600 mt-2">{error}</p>
            <p className="text-gray-500 mt-4">Redirecting to homepage...</p>
          </div>
        </div>
      </HelmetProvider>
    );
  }

  // Loading state
  if (!project || !components) {
    return (
      <HelmetProvider>
        <SEOHead 
          title="Loading Property Details"
          description="Please wait while we load the property information and details for you."
        />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Loading...</h1>
            <p className="text-gray-600 mt-2">Please wait while we prepare the page</p>
          </div>
        </div>
      </HelmetProvider>
    );
  }

  // Get hero component for SEO data
  const heroComponent = components.find(c => c.componentType === 'hero');
  const projectOverviewComponent = components.find(c => c.componentType === 'projectOverview');
  
  // Create SEO data from project and hero content
  const seoTitle = heroComponent?.content?.title || project.name || 'Premium Property in Dubai';
  const seoSubtitle = heroComponent?.content?.subtitle || '';
  const seoDescription = projectOverviewComponent?.content?.description || 
    `${seoTitle}. ${seoSubtitle}`.trim() || 
    `Discover ${project.name} - a premium real estate opportunity in Dubai. Get detailed information, floor plans, and investment options.`;
  
  const seoImage = heroComponent?.content?.backgroundImage || 
    'https://springfield-re.com/storage/burj-vista/img17.jpg';

  // Render dynamic components
  const renderComponent = (component: any) => {
    switch (component.componentType) {
      case 'hero':
        return (
          <section id="hero" key={component._id}>
            <HeroWithImage
              component={component}
              project={project}
              language={language}
              components={components}
            />
          </section>
        );
      case 'projectHighlights':
        return (
          <section id="highlights" key={component._id}>
            <ProjectHighlights
              features={component.content.features || []}
              developer={component.content.developer || undefined}
              language={currentLanguage}
            />
          </section>
        );
      case 'projectOverview':
        return (
          <section id="overview" key={component._id}>
            <ProjectOverviewWithImages
              component={component}
              currentLanguage={currentLanguage}
              navigationConfig={navigationConfig}
            />
          </section>
        );
      case 'leadForm':
        return (
          <section id="leadForm" key={component._id}>
            <DynamicLeadForm
              projectId={project._id as Id<"projects">}
              language={language || 'en'}
              contentLabels={navigationConfig?.contentLabels}
              content={component.content}
            />
          </section>
        );
      case 'faq':
        return (
          <section id="faq" key={component._id}>
            <FAQ
              faqs={component.content.faqs || []}
              language={currentLanguage}
              contentLabels={navigationConfig?.contentLabels}
            />
          </section>
        );
      case 'location':
        return (
          <section id="location" key={component._id}>
            <Location
              projectName={component.content.projectName || ''}
              coordinates={component.content.coordinates || { lat: 25.069750, lng: 55.247471 }}
              nearbyPlaces={component.content.nearbyPlaces || []}
              keyDistances={component.content.keyDistances || []}
              language={currentLanguage}
              contentLabels={navigationConfig?.contentLabels}
            />
          </section>
        );
      case 'paymentPlan':
        return (
          <section id="paymentPlan" key={component._id}>
            <PaymentPlan
              title={component.content.title || ''}
              description={component.content.description || ''}
              plans={component.content.plans || []}
              language={currentLanguage}
              contentLabels={navigationConfig?.contentLabels}
            />
          </section>
        );
      case 'projectGallery':
        return (
          <section id="gallery" key={component._id}>
            <ProjectGalleryWithImages
              component={component}
              currentLanguage={currentLanguage}
              navigationConfig={navigationConfig}
            />
          </section>
        );
      case 'legalInfo':
        return (
          <section id="legalInfo" key={component._id}>
            <LegalInfo
              dldPermitNumber={component.content.dldPermitNumber || ''}
              qrCodeUrl={component.content.qrCodeUrl || ''}
              language={currentLanguage}
              contentLabels={navigationConfig?.contentLabels}
            />
          </section>
        );
      // Additional component types will be added here later
      default:
        return null;
    }
  };

  // Order components based on LandingPage.tsx order
  const getComponentTypeOrder = (type: string) => {
    const orderMap: Record<string, number> = {
      'hero': 1,
      'projectHighlights': 2,
      'projectGallery': 3,
      'projectOverview': 4,
      'paymentPlan': 5,
      'location': 6,
      'leadForm': 7,
      'legalInfo': 8,
      'faq': 9
    };
    return orderMap[type] || 999; // Default to end if not found
  };

  // Sort components to match LandingPage.tsx order
  const sortedComponents = [...components].sort((a, b) => 
    getComponentTypeOrder(a.componentType) - getComponentTypeOrder(b.componentType)
  );

  return (
    <HelmetProvider>
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        projectName={project.name}
        locale={language === 'ar' ? 'ar_AE' : 'en_US'}
      />
      <ToastProvider>
        <main className={`min-h-screen ${rtlClasses}`} dir={dirAttribute}>
          <Navigation language={currentLanguage} projectId={project._id} />
          
          {/* Render all active components in the correct order */}
          {sortedComponents.map(renderComponent)}
          
          <CookieConsent 
            onAccept={handleCookieAccept}
            onCustomize={handleCookieCustomize}
          />
          <StickyActions 
            projectId={project._id}
            language={language}
            downloadFormContent={components.find(c => c.componentType === 'downloadForm')?.content}
          />
        </main>
      </ToastProvider>
    </HelmetProvider>
  );
};

// Component wrapper to resolve image URLs for ProjectOverview
const ProjectOverviewWithImages: React.FC<{
  component: any;
  currentLanguage: string;
  navigationConfig: any;
}> = ({ component, currentLanguage, navigationConfig }) => {
  const unitTypes = component.content.unitTypes || [];
  
  // Extract all file IDs from unit types
  const fileIds = unitTypes.map((unit: any) => unit.imageId);
  
  // Get all resolved URLs using the multiple file URLs hook
  const resolvedUrls = useMultipleFileUrls(fileIds);
  
  // Combine the resolved URLs with the unit type data
  const unitTypesWithResolvedImages = unitTypes.map((unit: any, index: number) => {
    const resolvedUrl = resolvedUrls[index];
    return {
      ...unit,
      image: getBestImageUrl(unit.image, resolvedUrl) || unit.image || ''
    };
  });

  return (
    <ProjectOverview
      title={component.content.title}
      status={component.content.status}
      description={component.content.description}
      sellingPoints={component.content.sellingPoints || []}
      unitTypes={unitTypesWithResolvedImages}
      language={currentLanguage}
      contentLabels={navigationConfig?.contentLabels}
    />
  );
};

// Component wrapper to resolve image URLs for ProjectGallery
const ProjectGalleryWithImages: React.FC<{
  component: any;
  currentLanguage: string;
  navigationConfig: any;
}> = ({ component, currentLanguage, navigationConfig }) => {
  const images = component.content.images || [];
  
  // Extract all file IDs from images
  const fileIds = images.map((image: any) => image.imageId);
  
  // Get all resolved URLs using the multiple file URLs hook
  const resolvedUrls = useMultipleFileUrls(fileIds);
  
  // Combine the resolved URLs with the image data
  const imagesWithResolvedUrls = images.map((image: any, index: number) => {
    const resolvedUrl = resolvedUrls[index];
    return {
      ...image,
      url: getBestImageUrl(image.url, resolvedUrl) || image.url || ''
    };
  });

  return (
    <ProjectGallery
      images={imagesWithResolvedUrls}
      language={currentLanguage}
      contentLabels={navigationConfig?.contentLabels}
    />
  );
};

// Component wrapper to resolve background image for Hero
const HeroWithImage: React.FC<{
  component: any;
  project: any;
  language: string | undefined;
  components: any[];
}> = ({ component, project, language, components }) => {
  const resolvedBackgroundImage = useBestImageUrl(
    component.content.backgroundImage,
    component.content.backgroundImageId
  );

  // Find download form component if it exists
  const downloadFormComponent = components.find(c => c.componentType === 'downloadForm');

  return (
    <Hero
      title={component.content.title}
      subtitle={component.content.subtitle}
      highlights={component.content.highlights || []}
      backgroundImage={resolvedBackgroundImage}
      primaryButtonText={component.content.primaryButtonText}
      secondaryButtonText={component.content.secondaryButtonText}
      showDownloadForm={component.content.showDownloadForm}
      projectId={project._id}
      language={language}
      downloadFormContent={downloadFormComponent?.content}
    />
  );
};

export default DynamicLandingPage; 